import { useCallback, useEffect, useRef, useState } from 'react';
import { emailService } from '@/services';
import type { EmailStatusFeedRow } from '@/services/email.service';
import { isReadingStatus } from '@/lib/emailReadState';
import { useEmailsChanged } from './useEmailsChanged';

/** Re-read cadence while something is still being read and the tab is visible. */
export const STATUS_FEED_POLL_MS = 15_000;

/**
 * Re-read cadence while nothing is being read and the tab is visible — how
 * the toast learns a NEW email exists when realtime is silent (YAR-477: in
 * prod the feed was read once and never again while two emails were read).
 */
export const STATUS_FEED_IDLE_POLL_MS = 30_000;

export interface UseEmailStatusFeedOptions {
  /**
   * Called after every realtime change on `emails` (debounced) — the shell
   * uses it to re-count Review and the plan meter, which is what the old
   * progress tracker's `onRefreshKey` did.
   */
  onEmailsChanged?: () => void;
  /**
   * Run the idle poll (no email reading). Only worth it for viewers who can
   * see the toast (Admin / Co-admin); defaults to false.
   */
  idlePoll?: boolean;
}

export interface UseEmailStatusFeedResult {
  emails: EmailStatusFeedRow[];
  /** False until the first read (the mount backfill) has answered. */
  loaded: boolean;
  refresh: () => Promise<void>;
}

/**
 * The emails the status toast narrates (YAR-477), kept live.
 *
 * Three sources, all silent, all re-running ONE query
 * (`emailService.getEmailStatusFeed`) so the query stays the single source of
 * truth:
 * - a mount backfill, so a run that finished while the app was closed still
 *   shows (the old tracker was realtime-only and never could);
 * - realtime on `emails`, unfiltered, on its own channel (the
 *   `useEmailsChanged` pattern: a `postgres_changes` filter cannot express
 *   the feed's IN predicate, so it is only a staleness hint);
 * - a 15 s poll while anything is still being read, a 30 s poll otherwise
 *   for viewers who can see the toast (visible tab only), plus a re-read on returning to the tab — realtime is
 *   best-effort (a channel can go silent for the whole session), and the
 *   5-minute server timeout moves a row without the client doing anything.
 */
export function useEmailStatusFeed(
  householdId: string | null | undefined,
  { onEmailsChanged, idlePoll = false }: UseEmailStatusFeedOptions = {},
): UseEmailStatusFeedResult {
  const [emails, setEmails] = useState<EmailStatusFeedRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!householdId) return;
    const request = ++requestRef.current;
    try {
      const rows = await emailService.getEmailStatusFeed(householdId);
      // Only the latest read may land; an older one resolving late would
      // put a stale status back on screen.
      if (request === requestRef.current) {
        setEmails(rows);
        setLoaded(true);
      }
    } catch {
      // Silent: a failed background read keeps what is on screen.
      if (request === requestRef.current) setLoaded(true);
    }
  }, [householdId]);

  useEffect(() => {
    setEmails([]);
    setLoaded(false);
    void refresh();
  }, [refresh]);

  const onEmailsChangedRef = useRef(onEmailsChanged);
  onEmailsChangedRef.current = onEmailsChanged;
  useEmailsChanged(
    () => {
      void refresh();
      onEmailsChangedRef.current?.();
    },
    { enabled: !!householdId, channel: 'email-status' },
  );

  const reading = emails.some((e) => isReadingStatus(e.status));
  useEffect(() => {
    if (!householdId || (!reading && !idlePoll)) return;
    const interval = setInterval(
      () => {
        if (document.visibilityState === 'visible') void refresh();
      },
      reading ? STATUS_FEED_POLL_MS : STATUS_FEED_IDLE_POLL_MS,
    );
    return () => clearInterval(interval);
  }, [householdId, reading, idlePoll, refresh]);

  useEffect(() => {
    if (!householdId) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [householdId, refresh]);

  return { emails, loaded, refresh };
}
